import { Check, CheckCircle, Clock, Copy, Eye } from 'lucide-react'
import { useState } from 'react'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from '@/shared/components/ui/drawer'
import { extractErrorMessage } from '@/shared/lib/error-utils'
import { toast } from '@/shared/lib/toast'
import type { Signal } from '../types/events.types'
import { SignalActionMenu } from './SignalActionMenu'

interface SignalDetailsDrawerProps {
  signal: Signal | null
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  onViewDetails?: (signal: Signal) => void
}

const getStatusIcon = (processed: boolean) => {
  return processed ? (
    <CheckCircle className='w-4 h-4 text-green-600' />
  ) : (
    <Clock className='w-4 h-4 text-yellow-600' />
  )
}

const formatDateTime = (dateString: string) => {
  const date = new Date(dateString)
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
}

export function SignalDetailsDrawer({
  signal,
  isOpen,
  onOpenChange,
  onViewDetails,
}: SignalDetailsDrawerProps) {
  const [copied, setCopied] = useState(false)

  const copyToClipboard = async () => {
    if (!signal) return

    try {
      await navigator.clipboard.writeText(JSON.stringify(signal, null, 2))
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (error) {
      toast.error(extractErrorMessage(error))
    }
  }

  if (!signal) return null

  return (
    <Drawer open={isOpen} onOpenChange={onOpenChange} direction='right'>
      <DrawerContent className='h-screen w-[50vw] min-w-[600px] max-w-[800px] ml-auto fixed inset-y-0 right-0 flex flex-col'>
        <DrawerHeader className='border-b flex-shrink-0 px-6 py-4 bg-background dark:bg-gray-900'>
          <DrawerTitle className='flex items-center gap-2 text-foreground'>
            <Eye className='w-5 h-5' />
            Signal Details
          </DrawerTitle>
          <DrawerDescription className='text-muted-foreground'>
            {signal.signal_name} • {formatDateTime(signal.timestamp)}
          </DrawerDescription>
        </DrawerHeader>

        <div className='flex-1 overflow-y-auto px-6 py-4 bg-background dark:bg-gray-900'>
          <div className='space-y-6'>
            {/* Signal Action Menu */}
            <div className='flex justify-end'>
              <SignalActionMenu
                signal={signal}
                onViewDetails={onViewDetails || (() => {})}
              />
            </div>

            {/* Signal Details Table */}
            <div>
              <div className='text-sm font-medium mb-3 text-foreground'>
                Signal Information
              </div>
              <div className='border rounded-lg overflow-hidden bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'>
                <table className='w-full text-sm'>
                  <tbody>
                    <tr className='border-b border-gray-200 dark:border-gray-600'>
                      <td className='py-3 px-4 font-medium text-gray-900 dark:text-gray-100 w-1/3'>
                        Signal ID
                      </td>
                      <td className='py-3 px-4 font-mono text-gray-700 dark:text-gray-300'>
                        {signal.signal_id || signal.id}
                      </td>
                    </tr>
                    <tr className='border-b border-gray-200 dark:border-gray-600'>
                      <td className='py-3 px-4 font-medium text-gray-900 dark:text-gray-100'>
                        Signal Name
                      </td>
                      <td className='py-3 px-4 text-gray-700 dark:text-gray-300'>
                        {signal.signal_name}
                      </td>
                    </tr>
                    <tr className='border-b border-gray-200 dark:border-gray-600'>
                      <td className='py-3 px-4 font-medium text-gray-900 dark:text-gray-100'>
                        Signal Type
                      </td>
                      <td className='py-3 px-4'>
                        <Badge
                          variant='secondary'
                          className={
                            signal.signal_type === 'cost_signal'
                              ? 'bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300'
                              : signal.signal_type === 'billable_signal'
                                ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
                                : 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                          }
                        >
                          {signal.signal_type
                            .replace('_', ' ')
                            .replace(/\b\w/g, l => l.toUpperCase())}
                        </Badge>
                      </td>
                    </tr>
                    {signal.agent_id && (
                      <tr className='border-b border-gray-200 dark:border-gray-600'>
                        <td className='py-3 px-4 font-medium text-gray-900 dark:text-gray-100'>
                          Agent ID
                        </td>
                        <td className='py-3 px-4 font-mono text-gray-700 dark:text-gray-300'>
                          {signal.agent_id}
                        </td>
                      </tr>
                    )}
                    {signal.feature_id && (
                      <tr className='border-b border-gray-200 dark:border-gray-600'>
                        <td className='py-3 px-4 font-medium text-gray-900 dark:text-gray-100'>
                          Feature ID
                        </td>
                        <td className='py-3 px-4 font-mono text-gray-700 dark:text-gray-300'>
                          {signal.feature_id}
                        </td>
                      </tr>
                    )}
                    <tr className='border-b border-gray-200 dark:border-gray-600'>
                      <td className='py-3 px-4 font-medium text-gray-900 dark:text-gray-100'>
                        Customer ID
                      </td>
                      <td className='py-3 px-4 font-mono text-gray-700 dark:text-gray-300'>
                        {signal.customer_id}
                      </td>
                    </tr>
                    <tr className='border-b border-gray-200 dark:border-gray-600'>
                      <td className='py-3 px-4 font-medium text-gray-900 dark:text-gray-100'>
                        Timestamp
                      </td>
                      <td className='py-3 px-4 text-gray-700 dark:text-gray-300'>
                        {formatDateTime(signal.timestamp)}
                      </td>
                    </tr>
                    {signal.value !== undefined && (
                      <tr className='border-b border-gray-200 dark:border-gray-600'>
                        <td className='py-3 px-4 font-medium text-gray-900 dark:text-gray-100'>
                          Value
                        </td>
                        <td className='py-3 px-4 text-gray-700 dark:text-gray-300'>
                          {signal.value}
                        </td>
                      </tr>
                    )}
                    <tr className='border-b border-gray-200 dark:border-gray-600'>
                      <td className='py-3 px-4 font-medium text-gray-900 dark:text-gray-100'>
                        Status
                      </td>
                      <td className='py-3 px-4'>
                        <div className='flex items-center gap-2'>
                          {getStatusIcon(signal.processed)}
                          <span className='text-sm text-gray-700 dark:text-gray-300'>
                            {signal.processed ? 'Processed' : 'Pending'}
                          </span>
                        </div>
                      </td>
                    </tr>
                    <tr className='border-b border-gray-200 dark:border-gray-600'>
                      <td className='py-3 px-4 font-medium text-gray-900 dark:text-gray-100'>
                        Library
                      </td>
                      <td className='py-3 px-4 text-gray-700 dark:text-gray-300'>
                        {signal.library} {signal.library_version}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Meta Table (for cost/billable signals) */}
            {signal.meta && Object.keys(signal.meta).length > 0 && (
              <div>
                <div className='text-sm font-medium mb-3 text-foreground'>
                  Meta
                </div>
                <div className='border rounded-lg overflow-hidden bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'>
                  <table className='w-full text-sm'>
                    <thead className='border-b border-gray-200 dark:border-gray-600 sticky top-0 bg-white dark:bg-gray-800'>
                      <tr>
                        <th className='text-left py-2 px-3 font-medium text-gray-900 dark:text-gray-100 w-1/3'>
                          Property
                        </th>
                        <th className='text-left py-2 px-3 font-medium text-gray-900 dark:text-gray-100 w-2/3'>
                          Value
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(signal.meta)
                        .filter(([_key, value]) => value != null)
                        .map(([key, value], _index) => (
                          <tr
                            key={key}
                            className='border-b border-gray-200 dark:border-gray-600'
                          >
                            <td className='py-2 px-3'>
                              <code className='text-xs bg-gray-100 dark:bg-gray-600 px-1.5 py-0.5 rounded font-mono text-gray-700 dark:text-gray-300'>
                                {key}
                              </code>
                            </td>
                            <td className='py-2 px-3 font-mono text-xs break-all max-w-0'>
                              <div
                                className='truncate text-gray-700 dark:text-gray-300'
                                title={String(value)}
                              >
                                {typeof value === 'object'
                                  ? JSON.stringify(value)
                                  : String(value)}
                              </div>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Raw JSON */}
            <div>
              <div className='flex items-center justify-between mb-2'>
                <div className='text-sm font-medium text-foreground'>
                  Raw JSON
                </div>
                <Button
                  variant='outline'
                  size='sm'
                  onClick={copyToClipboard}
                  className='h-7 px-2 text-xs'
                >
                  {copied ? (
                    <>
                      <Check className='w-3 h-3 mr-1' />
                      Copied
                    </>
                  ) : (
                    <>
                      <Copy className='w-3 h-3 mr-1' />
                      Copy JSON
                    </>
                  )}
                </Button>
              </div>
              <pre className='bg-slate-100 dark:bg-slate-800 p-4 rounded-lg text-sm font-mono overflow-auto max-h-64 text-slate-700 dark:text-slate-300'>
                {JSON.stringify(signal, null, 2)}
              </pre>
            </div>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  )
}
